import { ChangeDetectionStrategy, Component, ElementRef, inject, OnInit, signal, ViewChild } from '@angular/core';
import { LucideAngularModule, UploadCloud, File, X, CheckCircle, ExternalLink, Trash2 } from 'lucide-angular';
import { ButtonComponent } from '@shared/components/ui/button/button.component';
import { LoadingSpinnerComponent, ProjectStepperService } from '@shared';
import { ProjectService } from '@admin';
import { ToastService } from '@shared';
import { interval } from 'rxjs';
import { takeWhile } from 'rxjs/operators';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

/** File extension pattern accepted by the upload step. */
const ACCEPTED_EXT = /\.pdf$/i;
/** Maximum accepted upload size, in bytes. */
const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;
/** Leading bytes of the PDF file signature ("%PDF-"), used to sniff the real file type. */
const PDF_MAGIC_BYTES = [0x25, 0x50, 0x44, 0x46, 0x2d];

/**
 * Handles floor-plan PDF selection, upload, deletion, and upload progress UI.
 */
@Component({
  selector: 'app-step-upload-plan',
  imports: [LucideAngularModule, ButtonComponent, LoadingSpinnerComponent, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './step-upload-plan.component.html',
})
export class StepUploadPlanComponent implements OnInit {
  /** Native hidden file input opened by the upload button/dropzone. */
  @ViewChild('fileInput') fileInputRef!: ElementRef<HTMLInputElement>;

  /** Template icon for upload/dropzone actions. */
  readonly UploadCloudIcon    = UploadCloud;
  /** Template icon for document rows. */
  readonly FileIcon           = File;
  /** Template icon for remove actions. */
  readonly XIcon              = X;
  /** Template icon for completed upload state. */
  readonly CheckCircleIcon    = CheckCircle;
  /** Template icon for opening uploaded documents. */
  readonly ExternalLinkIcon   = ExternalLink;
  /** Template icon for delete actions. */
  readonly TrashIcon          = Trash2;

  /** Project document API client. */
  private readonly projectService = inject(ProjectService);
  /** Toast notifications for upload/delete outcomes. */
  private readonly toast          = inject(ToastService);
  private readonly translate      = inject(TranslateService);
  /** Shared stepper state for upload and existing documents. */
  readonly stepper                = inject(ProjectStepperService);

  /** Whether a file is currently hovering over the dropzone. */
  readonly dragOver        = signal(false);
  /** Current upload validation or request error. */
  readonly error           = signal<string | null>(null);
  /** Whether an upload request is in progress. */
  readonly uploading       = signal(false);
  /** Display progress value from 0 to 100. */
  readonly uploadProgress  = signal(0);
  /** Document id currently being deleted. */
  readonly deleting        = signal<number | null>(null);

  /** Current selected file signal from the shared stepper state. */
  get selectedFile() { return this.stepper.uploadedFile; }

  /**
   * Loads existing project documents when the upload step is shown.
   */
  ngOnInit(): void {
    this.stepper.loadExistingDocuments();
  }

  /**
   * Extracts a readable filename from a document URL.
   *
   * @param url Document URL from the API.
   */
  filenameFromUrl(url: string): string {
    try {
      const parts = new URL(url).pathname.split('/');
      return decodeURIComponent(parts[parts.length - 1]);
    } catch {
      return url.split('/').pop() ?? url;
    }
  }

  /**
   * Opens the hidden native file browser.
   */
  openFileBrowser(): void {
    this.fileInputRef.nativeElement.click();
  }

  /**
   * Handles file selection from the native file input.
   *
   * @param event Input change event.
   */
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file  = input.files?.[0] ?? null;
    void this.processFile(file);
    input.value = '';
  }

  /**
   * Marks the dropzone as active while a dragged file is over it.
   *
   * @param event Native drag event.
   */
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.dragOver.set(true);
  }

  /**
   * Clears the active dropzone state when dragging leaves.
   */
  onDragLeave(): void {
    this.dragOver.set(false);
  }

  /**
   * Processes a dropped file from the dropzone.
   *
   * @param event Native drop event.
   */
  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragOver.set(false);
    const file = event.dataTransfer?.files[0] ?? null;
    void this.processFile(file);
  }

  /**
   * Deletes an existing uploaded main document.
   *
   * @param docId Document id to delete.
   */
  deleteDoc(docId: number): void {
    this.deleting.set(docId);
    this.projectService.deleteDocument({ doc_id: docId, doc_type: 'main' }).subscribe({
      next: () => {
        this.deleting.set(null);
        this.stepper.loadExistingDocuments(true);
        this.toast.success(
          this.translate.instant('PROJECT_STEPPER.STEP2.TOAST.DELETE_SUCCESS_TITLE'),
          this.translate.instant('PROJECT_STEPPER.STEP2.TOAST.DELETE_SUCCESS_MESSAGE'),
        );
      },
      error: () => {
        this.deleting.set(null);
        this.toast.error(
          this.translate.instant('PROJECT_STEPPER.STEP2.TOAST.DELETE_FAILED_TITLE'),
          this.translate.instant('PROJECT_STEPPER.STEP2.TOAST.DELETE_FAILED_MESSAGE'),
        );
      },
    });
  }

  /**
   * Clears the selected pending file and upload state.
   */
  removeFile(): void {
    this.stepper.uploadedFile.set(null);
    this.stepper.uploadComplete.set(false);
    this.error.set(null);
  }

  /**
   * Formats a byte count into a compact file-size label.
   *
   * @param bytes Raw file size in bytes.
   */
  formatSize(bytes: number): string {
    if (bytes < 1024)        return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  /**
   * Validates and stores a selected file before upload.
   *
   * @param file File from input/drop, or null when none was provided.
   */
  private async processFile(file: File | null): Promise<void> {
    if (!file) return;
    this.error.set(null);
    this.stepper.uploadComplete.set(false);

    if (!ACCEPTED_EXT.test(file.name)) {
      this.error.set(this.translate.instant('PROJECT_STEPPER.STEP2.ERRORS.UNSUPPORTED_TYPE'));
      return;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      this.error.set(this.translate.instant('PROJECT_STEPPER.STEP2.ERRORS.FILE_TOO_LARGE', {
        maxSizeMb: Math.round(MAX_FILE_SIZE_BYTES / (1024 * 1024)),
      }));
      return;
    }
    if (!(await this.hasPdfSignature(file))) {
      this.error.set(this.translate.instant('PROJECT_STEPPER.STEP2.ERRORS.INVALID_PDF'));
      return;
    }
    this.stepper.uploadedFile.set(file);
    this.upload(file);
  }

  /**
   * Sniffs the file's leading bytes for the "%PDF-" signature, since a `.pdf`
   * extension alone can be spoofed by renaming any file.
   *
   * @param file File to inspect.
   */
  private async hasPdfSignature(file: File): Promise<boolean> {
    try {
      const header = new Uint8Array(await file.slice(0, PDF_MAGIC_BYTES.length).arrayBuffer());
      return PDF_MAGIC_BYTES.every((byte, i) => header[i] === byte);
    } catch {
      return false;
    }
  }

  /**
   * Uploads a validated PDF as the project's main document.
   *
   * @param file PDF file to upload.
   */
  private upload(file: File): void {
    const projectId = this.stepper.project()?.projectId;
    if (!projectId) {
      this.error.set(this.translate.instant('PROJECT_STEPPER.ERRORS.NO_PROJECT_RESTART'));
      return;
    }

    this.uploading.set(true);
    this.uploadProgress.set(0);

    // Animate a fake progress bar up to 88% while the request is in flight.
    // Real XHR UploadProgress events are coalesced with the response in Angular's
    // scheduler, so the intermediate "uploading" state would never render otherwise.
    let simulated = 0;
    const progressSub = interval(180)
      .pipe(takeWhile(() => simulated < 88))
      .subscribe(() => {
        simulated = Math.min(88, simulated + Math.random() * 7 + 3);
        this.uploadProgress.set(Math.round(simulated));
      });

    this.projectService.replaceMainDocument(projectId, file).subscribe({
      next: () => {},
      complete: () => {
        progressSub.unsubscribe();
        this.uploadProgress.set(100);
        // Immediately invalidate stale detection state so any in-flight hydration can't overwrite it
        this.stepper.newDocumentUploaded.set(true);
        this.stepper.step3DataLoaded.set(false);
        this.stepper.floorAssignments.set([]);
        this.stepper.submittedPageNumbers.set([]);
        this.stepper.detectionTriggered.set(false);
        this.stepper.categoryResults.set([]);
        this.stepper.resetPreProcessState();
        setTimeout(() => {
          this.uploading.set(false);
          this.stepper.uploadComplete.set(true);
          this.stepper.loadExistingDocuments(true);
          this.toast.success(
            this.translate.instant('PROJECT_STEPPER.STEP2.TOAST.UPLOAD_SUCCESS_TITLE'),
            this.translate.instant('PROJECT_STEPPER.STEP2.TOAST.UPLOAD_SUCCESS_MESSAGE'),
          );
        }, 400);
      },
      error: () => {
        progressSub.unsubscribe();
        this.uploading.set(false);
        this.uploadProgress.set(0);
        this.stepper.uploadedFile.set(null);
        this.error.set(this.translate.instant('PROJECT_STEPPER.STEP2.ERRORS.UPLOAD_FAILED'));
        this.toast.error(
          this.translate.instant('PROJECT_STEPPER.STEP2.TOAST.UPLOAD_FAILED_TITLE'),
          this.translate.instant('PROJECT_STEPPER.STEP2.TOAST.UPLOAD_FAILED_MESSAGE'),
        );
      },
    });
  }
}
